import * as React from "react";
import {TriStateCheckbox} from "@bizform/primereact/components/tristatecheckbox/TriStateCheckbox";
import {TriStateCheckboxProps} from "@bizform/primereact/tristatecheckbox";
import {RadioButton} from "@bizform/primereact/radiobutton";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {DataType} from "@bizform/type/model/model/DataType.model";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {ComponentHelper, Debug, ObjectHelper} from "@bizform/helper/dist/js/bizflow/helper.min";
import {ComponentElementClass, ReactComponentElementInit} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";


type Props = TriStateCheckboxProps & {
};

type WrappedCompState = {
    // attribute
    inputId?: string;
    tabindex?: number;
    value?: null | boolean;
    name?: string;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    tooltipAttribute?: TooltipOptions & {text?: string};
    disabled?: boolean;
    readOnly?: boolean;

    // event
    onChange?(e: ComponentEventType): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * TriStateCheckbox component element
 *
 * @since 0.0.1
 * @author Byungmin Choi
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null | boolean>>()
export class BfCheckboxTriState extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_GTqUU8aTVAmaggTCx4aTpwNu1lIOpiT",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.checkboxTristate.label,
        description: I18n.componentElement.checkboxTristate.description,
        icon: "",
        dataType: [DataType.TRISTATE, DataType.BOOLEAN, DataType.FILE, DataType.SIGN],
        property: null
    };

    focus(): void {
        if (this.accessibility) {
            this._tristateCheckbox.querySelector("input").focus();
        } else if (this._tristateCheckbox && this._tristateCheckbox.inputEL) {
            this._tristateCheckbox.inputEL.focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: null | boolean): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: null | boolean): void {
        if (this.dataType !== DataType.TRISTATE) {
            if (value !== true && value !== false) {
                value = null;
            }
        }
        if (ComponentHelper.isNull(value)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: null
                }
            };
            this.setState(newState);
        } else if ( typeof value === "boolean") {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value
                }
            };
            this.setState(newState);
        } else if (typeof value === "number" && ( value === 0 || value === 1)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: value ? true : false
                }
            };
            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    @ReactComponentElementInit(BfCheckboxTriState)
    static init(): ComponentElement<null | boolean> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _tristateCheckbox: any;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                value: null
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    componentDidUpdate(): void {
        if (this._tristateCheckbox) {
            this._makeStyle();
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, Array.from((this.accessibility ? this._tristateCheckbox : this._tristateCheckbox.element).querySelectorAll("input")), this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
        if (this._tristateCheckbox && this._tristateCheckbox.element) {
            if (this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
                this._tristateCheckbox.element.classList.add("p-disabled");
            }
        }
    }

    componentWillUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>) {
        this._removeCustom();
    }

    componentWillUnmount(): void {
        this._removeCustom();
        this._tristateCheckbox = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(e: ComponentEventType): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            // change state and then emit
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    value: e.value
                }
            };

            // setState is an async function
            this.setState(newState, this._callbackSetState);
        }
    }

    private _callbackSetState() {
        if (this.state.wrappedCompState.value === null) {
            this.emit(null);
        } else if (this.state.wrappedCompState.value) {
            this.emit(true);
        } else {
            this.emit(false);
        }
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4Edit();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Edit();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: WrappedCompState): React.ReactNode {
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        delete(newProps.printClassName);
        delete(newProps.printStyle);
        if (this.accessibility) {
            return (
                <div ref={this._setRef} className="p-inputgroup">
                    {this._radio(newProps, 0, true, newProps.value === true)}
                    {this._radio(newProps, 1, false, newProps.value === false)}
                    {this._radio(newProps, 2, null, newProps.value === null)}
                </div>
            );
        } else {
            return (
                <TriStateCheckbox
                    ref={this._setRef}
                    {...newProps}
                    key={key}
                    inputId={this.elementId}
                    style={null}
                />
            );
        }
    }

    private _radio(newProps: WrappedCompState, index: number, value: any, checked: boolean): React.ReactNode {
        return (
            <React.Fragment>
                <RadioButton
                    {...newProps}
                    key={`${this.elementId}_${index}`}
                    inputId={`${this.elementId}_${index}`}
                    checked={checked}
                    value={value}
                    onChange={newProps.onChange}
                />
                <label htmlFor={`${this.elementId}_${index}`} className="p-radiobutton-label" style={{cursor: "pointer", marginRight: "1rem"}}>{String(value)}</label>
            </React.Fragment>
        );
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className= {`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{`${this.state.wrappedCompState.value}`}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._tristateCheckbox = ref;
            this._makeStyle();
            this.readyComponent();
        }
    }

    private _removeCustom(): void {
        if (this._tristateCheckbox && this._tristateCheckbox.element) {
            if (this._tristateCheckbox.element.classList.contains("p-disabled")) {
                this._tristateCheckbox.element.classList.remove("p-disabled");
            }
        }
    }

    private _makeStyle(newProps = {...this.state.wrappedCompState}): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT || this.state.displayMode === ComponentElementDisplayMode.READONLY || this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
            if (!this.accessibility && this._tristateCheckbox && this._tristateCheckbox.element) {
                let span = this._tristateCheckbox.element.querySelectorAll("div")[1];
                span.setAttribute("style", "");
                for (let item in newProps.style) {
                    (span as any).style[item] = (newProps as any).style[item];
                }
            }
        }
    }
}
